/* 오른쪽 AI 패널 — 대화로 편집 화면 고치기 · 요소 첨부(◎ 요소 선택) · 되돌리기 · 오늘 토큰 사용량 */
import { IcAi, IcBolt, IcClose, IcTarget } from './icons';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, SidePanel, Tag, Textarea } from '@indipia/ui-core';
import { showcaseApi, type UsageSummary } from '../api/showcaseApi';
import { attachLabel, sendChat } from '../ai/agent';
import { currentTemplate, ed, locateElement, undoLast, useEd } from '../store/editor';
import { chipRow, cx, iconBtn, popHead, popTitle, popover, pulse } from './ui';

const LOG: Record<string, string> = { ok: 'font-semibold text-[var(--status-success-700)]', skip: 'text-text-secondary', wait: 'text-text-secondary', run: 'font-semibold text-group-onSubtle', fail: 'text-error' };
const ROW = 'flex justify-between gap-sm border-b border-border py-02 tabular-nums [&_small]:text-caption [&_small]:text-text-secondary';

const ICON: Record<string, string> = { ok: '✓', skip: '–', wait: '·', run: '✎', fail: '✕' };
const fmtTok = (n: number) => (n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e4 ? (n / 1e3).toFixed(1) + 'K' : (n || 0).toLocaleString());

export function AiDock() {
  const e = useEd();
  const [input, setInput] = useState('');
  const msgRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const m = msgRef.current; if (m) m.scrollTop = m.scrollHeight; }, [e.chat.length, e.rev, e.dockOpen]);
  const toggle = () => { ed.dockOpen = !ed.dockOpen; ed.bump(); };
  e.attached = e.attached.filter((x) => locateElement(x) >= 0);
  const send = () => {
    if (e.busy) { void sendChat(''); return; }
    const m = input.trim();
    if (!m) return;
    setInput('');
    void sendChat(m);
  };
  const target = e.attached.length
    ? `첨부한 요소 ${e.attached.length}개에만 적용돼요 — AI에겐 이 요소만 보여서 다른 글·사진·페이지는 바뀌지 않아요.`
    : `전체 페이지 (${e.pages.length}페이지 · 지금 ${e.cur + 1}페이지) — 특정 요소만 고치려면 아래 '요소 선택'으로 골라 첨부하세요.`;
  return (
    <SidePanel open={e.dockOpen} side="right" label="AI 패널" className="[&_.side-panel-body]:flex [&_.side-panel-body]:flex-col"
      title={(
        <div className="flex w-full items-center gap-sm">
          <span className="inline-flex flex-1 items-center gap-xs whitespace-nowrap [&>svg]:text-group"><IcAi size={20} />AI 어시스턴트</span>
          <UsageChip />
          <Button size="sm" variant="ghost" className={iconBtn} aria-label="AI 패널 닫기" title="AI 패널 닫기" onClick={toggle}><IcClose size={16} /></Button>
        </div>
      )}>
      <div className="flex min-h-0 flex-1 flex-col gap-sm p-04">
        <div className="flex items-center gap-sm rounded-md bg-surface p-03 text-caption"><span className="font-semibold text-text-secondary">템플릿</span><strong className="min-w-0 flex-1 truncate">{currentTemplate().name}</strong></div>
        <Alert kind="info" className="text-caption">{target}</Alert>
        <div className="flex min-h-13 flex-1 flex-col gap-sm overflow-y-auto px-01 py-02" ref={msgRef} role="log" aria-live="polite" aria-label="AI 대화">
          {e.chat.map((m, i) => (
            <div key={i} className={cx('max-w-[92%] whitespace-pre-wrap rounded-lg px-04 py-03 text-caption leading-normal',
              m.role === 'user' ? 'self-end rounded-br-xs bg-group text-group-onPrimary' : m.role === 'system' ? 'max-w-full self-center bg-transparent text-center text-text-secondary' : 'self-start rounded-bl-xs bg-surface text-text')}>
              {m.text}
              {!!m.attach?.length && <span className="mt-02 block opacity-80">첨부 {m.attach.map((l, k) => `${k + 1}. ${l}`).join('  ')}</span>}
              {!!m.log?.length && <div className="mt-02 flex flex-col gap-xs border-t border-border pt-02 leading-snug">{m.log.map((l, k) => <span key={k} className={LOG[l.state]}>{ICON[l.state]} {l.text}</span>)}</div>}
              {m.status && <div className={cx('mt-02 font-semibold', m.role === 'user' ? '' : 'text-group-onSubtle')}>{m.status}</div>}
              {!!m.usage?.total && (
                <div className="mt-02 tabular-nums text-text-secondary" title={`입력 ${m.usage.input.toLocaleString()} · 출력 ${m.usage.output.toLocaleString()} · 생각 ${m.usage.thinking.toLocaleString()}${m.usage.images ? ` · 사진 생성 ${m.usage.images}장` : ''}`}>
                  이 요청 {m.usage.total.toLocaleString()} 토큰 · AI {m.usage.calls}회
                </div>
              )}
              {!!m.undoIndex && m.undoIndex === e.undoStack.length && !e.busy && <Button size="sm" variant="etc" className="mt-03" onClick={undoLast}>↩ 되돌리기</Button>}
            </div>
          ))}
        </div>
        {!!e.attached.length && (
          <div className={chipRow} aria-label="첨부한 요소">
            {e.attached.map((x, i) => (
              <Tag key={x.id} className="max-w-full border-[var(--status-warning-200)] bg-[var(--status-warning-50)] text-[var(--status-warning-700)]" title={attachLabel(x)} onRemove={() => { ed.attached.splice(i, 1); ed.bump(); }}>
                <span className="inline-flex size-05 shrink-0 items-center justify-center rounded-full bg-[var(--status-warning-500)] font-bold text-[var(--gray-0)]">{i + 1}</span><span className="max-w-[calc(var(--spacing-13)+var(--spacing-08))] truncate">{attachLabel(x)}</span>
              </Tag>
            ))}
          </div>
        )}
        <Textarea data-chat-input textareaClassName="min-h-11 resize-y" aria-label="AI에게 요청" value={input} onChange={(ev) => setInput(ev.target.value)}
          placeholder={e.attached.length ? '예: 더 짧고 담백하게 / 가운데 정렬하고 남색으로 / 이 사진을 대표 사진으로'
            : '예: 거실 제목 가운데로 하고 욕실 문구는 한 문장으로 줄여줘 / 욕실 두 번째 사진을 대표 사진으로 / 이 시공사례 어때?'}
          onKeyDown={(ev) => { if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) { ev.preventDefault(); send(); } }} />
        <div className="flex items-center justify-between gap-sm">
          <Button size="sm" variant={e.picking ? 'secondary' : 'etc'} aria-pressed={e.picking} title="페이지에서 요소를 클릭해 대화에 첨부해요. Shift+클릭으로 여러 개, Esc로 취소"
            onClick={() => { ed.picking = !ed.picking; if (ed.picking) ed.selectedId = null; ed.bump(); }}>
            <IcTarget size={16} />{e.picking ? '선택 중… (Esc)' : '요소 선택'}
          </Button>
          <Button size="sm" variant={e.busy ? 'danger' : 'primary'} onClick={send}>{e.busy ? '■ 중지' : '보내기'}</Button>
        </div>
      </div>
    </SidePanel>
  );
}

/** 오늘 쓴 AI 토큰(회사) — AI 호출이 끝날 때마다, 열려 있으면 10초마다 새로 */
function UsageChip() {
  const e = useEd();
  const [sum, setSum] = useState<UsageSummary | null>(null);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let alive = true;
    showcaseApi.usageToday().then((s) => {
      if (!alive) return;
      setSum((prev) => { if (prev && s.total !== prev.total) pulse(wrap.current); return s; });
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [e.usageTick]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => { ed.usageTick++; ed.bump(); }, 10000);
    const key = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', key);
    return () => { clearInterval(t); document.removeEventListener('keydown', key); };
  }, [open]);
  const r = wrap.current?.getBoundingClientRect();
  const time = (iso: string) => new Date(iso).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const feats = sum ? Object.entries(sum.byType || {}).sort((a, b) => b[1].total - a[1].total) : [];
  return (
    <span ref={wrap} className="inline-flex [&_.btn]:tabular-nums">
      <Button size="sm" variant="etc" aria-haspopup="dialog" aria-expanded={open} title="오늘 쓴 AI 토큰 — 눌러서 자세히 보기" onClick={() => setOpen(!open)}>
        <IcBolt size={16} />오늘 {sum ? fmtTok(sum.total) : '0'} 토큰
      </Button>
      {open && sum && r && (
        <div className={cx(popover, 'max-h-[70vh] w-[calc(var(--spacing-13)*2+var(--spacing-05))] overflow-auto text-caption')} role="dialog" aria-label="오늘 쓴 AI 토큰" style={{ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) }}>
          <div className={popHead}><b>오늘 쓴 AI 토큰 (우리 회사)</b>
            <Button size="sm" variant="ghost" className={iconBtn} aria-label="닫기" onClick={() => setOpen(false)}><IcClose size={16} /></Button></div>
          <div className="text-display-sm font-bold tabular-nums">{(sum.total || 0).toLocaleString()}</div>
          <div className="grid grid-cols-3 gap-sm [&>div]:rounded-md [&>div]:bg-surface [&>div]:p-03 [&_b]:block [&_b]:text-body-sm [&_b]:tabular-nums [&_span]:text-text-secondary">
            <div><b>{fmtTok(sum.input)}</b><span>입력{sum.imageInput ? ` (사진 ${fmtTok(sum.imageInput)})` : ''}</span></div>
            <div><b>{fmtTok(sum.output)}</b><span>출력</span></div>
            <div><b>{fmtTok(sum.thinking)}</b><span>생각</span></div>
            <div><b>{sum.calls}</b><span>AI 호출</span></div>
            <div><b>{sum.images}</b><span>사진 생성</span></div>
            <div><b>{sum.searches}</b><span>구글 검색</span></div>
          </div>
          <div className={popTitle}>기능별</div>
          {feats.length ? feats.map(([k, v]) => <div key={k} className={ROW}><span>{v.label || k} <small>{v.calls}회</small></span><b>{v.total.toLocaleString()}</b></div>)
            : <div className={ROW}><small>아직 사용 기록이 없어요</small></div>}
          <div className={popTitle}>최근 호출</div>
          {(sum.recent || []).slice(0, 15).map((x, i) => <div key={i} className={ROW}><span>{x.label || x.aiType} <small>{time(x.at)}</small></span><b>{(x.total || 0).toLocaleString()}</b></div>)}
        </div>
      )}
    </span>
  );
}
